import { useState, useRef, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Info, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFieldOpsStore } from '@/stores/fieldOpsStore';
import { useCampaignStore } from '@/stores/campaignStore';
import { useMapboxToken } from '@/hooks/useMapboxToken';
import { useSignalR } from '@/hooks/useSignalR';
import type { ContactCreatedEvent } from '@signalvote/shared';
import { queryKeys } from '@/lib/queryKeys';
import { skip } from '@signalvote/data';
import { selectedCampaignKey } from '@/lib/data';
import {
  useWalkbooksWithPolygons,
  useWalkbookHouses,
  useCreateWalkbook,
  useUpdatePolygon,
  usePreviewPolygon,
  useVoterMapPoints,
  useWalkbookContactRates,
} from '@/hooks/queries';
import TurfMap from './TurfMap';
import type { TurfMapHandle } from './TurfMap';
import { TurfListPanel } from './TurfListPanel';
import { TurfDetailPanel } from './TurfDetailPanel';
import { TurfCutPanel } from './TurfCutPanel';
import { SaveTurfDialog } from './SaveTurfDialog';
import { LiveTrackingToggle } from './LiveTrackingToggle';
import { MapLayerPanel } from './MapLayerPanel';
import { VoterFilterPanel } from './VoterFilterPanel';
import { VoterDetailPopup } from './VoterDetailPopup';
import {
  HouseStatus,
  TurfStatus,
  hasActiveFilters,
  getWalkbookTurfStatus,
  parsePolygon,
} from '@signalvote/shared';
import type { GeoJSONPolygon, Turf, VoterHouse } from '@signalvote/shared';

/**
 * FieldOpsView — Main orchestrator component for field operations.
 *
 * Manages two modes:
 * - **management**: Turf list + map + optional detail panel
 * - **cutting**: Turf cut panel + map with drawing enabled
 *
 * Canvassing itself happens in the mobile app; the coordinator watches it here
 * through the live-canvasser layer.
 */
export function FieldOpsView() {
  // ---------------------------------------------------------------------------
  // Local state
  // ---------------------------------------------------------------------------
  const [drawPolygon, setDrawPolygon] = useState<GeoJSONPolygon | null>(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [editingTurfId, setEditingTurfId] = useState<string | null>(null);
  const [selectedVoter, setSelectedVoter] = useState<{
    ids: string[];
    lngLat: [number, number];
  } | null>(null);

  // ---------------------------------------------------------------------------
  // Store (UI-only state)
  // ---------------------------------------------------------------------------
  const {
    mode,
    setMode,
    selectedTurfId,
    selectTurf,
    showLiveCanvassers,
    toggle,
    setLastCreatedTurfId,
    showVoterPins,
    showTurfs,
    showContactRate,
    showDemographics,
    demographicMetric,
    showElectionOverlay,
    showMajorRoads,
    voterFilters,
    clearVoterFilters,
  } = useFieldOpsStore();

  // ---------------------------------------------------------------------------
  // Campaign store selectors
  // ---------------------------------------------------------------------------
  const subsidiaryCode = useCampaignStore((s) => s.subsidiaryCode);
  const campaignId = useCampaignStore((s) => s.selectedCampaign?.id ?? null);
  const canManageFieldOps = useCampaignStore((s) => s.canManage('FieldOps'));

  // ---------------------------------------------------------------------------
  // Data hooks
  // ---------------------------------------------------------------------------
  const walkbooksRead = useWalkbooksWithPolygons(undefined);
  const walkbooks = useMemo(() => (walkbooksRead.status === 'ready' ? walkbooksRead.data : []), [walkbooksRead]);
  const walkbooksLoading = walkbooksRead.status === 'loading' || walkbooksRead.status === 'idle';
  const housesRead = useWalkbookHouses(mode === 'management' && selectedTurfId ? selectedTurfId : skip);
  const selectedTurfHouses = useMemo(() => (housesRead.status === 'ready' ? housesRead.data : []), [housesRead]);
  const editingWalkbook = editingTurfId
    ? walkbooks.find((w) => w.id === editingTurfId)
    : undefined;

  const { mutate: createWalkbook } = useCreateWalkbook();
  const { mutate: updatePolygon } = useUpdatePolygon();
  const { mutate: previewPolygon, reset: resetPreview, data: previewData, isPending: isPreviewPending } = usePreviewPolygon();
  const voterMapPointsRead = useVoterMapPoints(mode === 'management' || mode === 'cutting' ? voterFilters : skip);
  const voterMapPoints = voterMapPointsRead.status === 'ready' ? voterMapPointsRead.data : undefined;
  const voterPointsFetching =
    voterMapPointsRead.status === 'loading' || (voterMapPointsRead.status === 'ready' && voterMapPointsRead.refreshing);
  const { data: contactRates } = useWalkbookContactRates();

  // ---------------------------------------------------------------------------
  // Mapbox token
  // ---------------------------------------------------------------------------
  const envToken = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;
  const { token: fetchedToken, isLoading: tokenLoading, error: tokenError } = useMapboxToken(!envToken);
  const token = envToken || fetchedToken;

  // ---------------------------------------------------------------------------
  // Map data transforms
  // ---------------------------------------------------------------------------

  /** Convert walkbooks to Turf[] shape for the map (parse polygon strings) */
  const activeTurfs = useMemo<Turf[]>(() => {
    const result: Turf[] = [];
    for (const wb of walkbooks) {
      const polygon = parsePolygon(wb.polygon);
      if (!polygon) continue;
      result.push({
        id: wb.id,
        name: wb.name,
        polygon,
        houseCount: wb.houseCount,
        voterCount: wb.voterCount,
        shiftId: undefined,
        teamId: undefined,
        volunteerIds: wb.assignedVolunteers.map((v) => v.id),
        doorsKnocked: Math.round((wb.progress / 100) * wb.houseCount),
        totalDoors: wb.houseCount,
        createdAt: wb.createdAt,
        isArchived: false,
      });
    }
    return result;
  }, [walkbooks]);

  /** Get turf status using the utility function */
  const getTurfStatus = useCallback(
    (id: string) => {
      const wb = walkbooks.find((w) => w.id === id);
      if (!wb) return TurfStatus.Unassigned;
      // Session-primary: pass the turf's current-shift status so a live session's colour
      // matches the panel. Absent → the helper falls back to its progress-based headline.
      return getWalkbookTurfStatus(wb, wb.currentShiftStatus);
    },
    [walkbooks],
  );

  /** Convert selected turf houses to VoterHouse[] for management map */
  const selectedTurfVoterHouses = useMemo<VoterHouse[]>(() => {
    return selectedTurfHouses.map((h) => ({
      id: h.id,
      address: h.address,
      coordinate: [h.longitude, h.latitude] as [number, number],
      voterCount: h.voterCount,
      isAssigned: h.status !== HouseStatus.NotVisited,
    }));
  }, [selectedTurfHouses]);

  // Live canvasser tracking via SignalR — gate on both toggle and capability
  const queryClient = useQueryClient();
  const handleContactCreated = useCallback((event: ContactCreatedEvent) => {
    // Update voter map points cache in-place — flip the contacted voter's pin color
    queryClient.setQueriesData<import('@signalvote/shared').VoterMapPointDto[]>(
      { queryKey: selectedCampaignKey(['voters', 'map-points']) },
      (old) => {
        if (!old || !Array.isArray(old)) return old;
        return old.map((vp) =>
          vp.id === event.voterId ? { ...vp, isContacted: true } : vp,
        );
      },
    );
    // Refresh contact rate overlay
    queryClient.invalidateQueries({ queryKey: queryKeys.walkbooks.contactRates() });
  }, [queryClient]);

  // Always connect SignalR when we have a campaign — contact events update dot
  // colors regardless of whether live canvasser tracking is toggled on.
  const { positions: rawCanvasserPositions, activeCount, connectionState } = useSignalR({
    campaignId,
    subsidiaryCode,
    enabled: !!campaignId,
    onContactCreated: handleContactCreated,
  });

  // Only pass canvasser positions to the map when the live toggle is on
  const canvasserPositions = showLiveCanvassers && canManageFieldOps
    ? rawCanvasserPositions
    : undefined;

  /** Transform voter map points to [lng, lat] GeoJSON order */
  const voterPoints = useMemo(() => {
    if (!voterMapPoints?.length) return undefined;
    return voterMapPoints.map((vp) => ({
      id: vp.id,
      coordinate: [vp.longitude, vp.latitude] as [number, number],
      isContacted: vp.isContacted,
    }));
  }, [voterMapPoints]);

  const mapRef = useRef<TurfMapHandle>(null);

  // ---------------------------------------------------------------------------
  // Preview polygon stats
  // ---------------------------------------------------------------------------
  const previewStats = previewData ?? { houseCount: 0, voterCount: 0 };

  // Trigger preview when polygon changes
  const handlePolygonForPreview = useCallback(
    (polygon: GeoJSONPolygon) => {
      setDrawPolygon(polygon);
      previewPolygon(JSON.stringify(polygon));
    },
    [previewPolygon],
  );

  // ---------------------------------------------------------------------------
  // Callbacks — mode transitions
  // ---------------------------------------------------------------------------

  const handleCutTurf = useCallback(() => {
    selectTurf(null);
    setDrawPolygon(null);
    resetPreview();
    setMode('cutting');
  }, [selectTurf, setMode, resetPreview]);

  const handleCancelCut = useCallback(() => {
    const wasEditing = editingTurfId;
    setDrawPolygon(null);
    setEditingTurfId(null);
    resetPreview();
    setMode('management');
    if (wasEditing) {
      selectTurf(wasEditing);
    }
  }, [editingTurfId, setMode, resetPreview, selectTurf]);

  const handleClearPolygon = useCallback(() => {
    setDrawPolygon(null);
    resetPreview();
    mapRef.current?.clearDraw();
  }, [resetPreview]);

  const handleSaveTurf = useCallback(() => {
    if (editingTurfId && drawPolygon) {
      // Editing existing turf — update polygon via API
      updatePolygon(
        { id: editingTurfId, polygon: JSON.stringify(drawPolygon) },
        {
          onSuccess: () => {
            setDrawPolygon(null);
            setEditingTurfId(null);
            setMode('management');
            selectTurf(editingTurfId);
            requestAnimationFrame(() => {
              mapRef.current?.fitToTurf(editingTurfId);
            });
          },
        },
      );
    } else {
      // Creating new turf — show name dialog
      setSaveDialogOpen(true);
    }
  }, [editingTurfId, drawPolygon, updatePolygon, setMode, selectTurf]);

  const handleSaveConfirm = useCallback(
    (name: string) => {
      if (!drawPolygon) return;

      createWalkbook(
        { name, polygon: JSON.stringify(drawPolygon) },
        {
          onSuccess: (newWalkbook) => {
            setSaveDialogOpen(false);
            setDrawPolygon(null);
            setMode('management');
            selectTurf(newWalkbook.id);
            setLastCreatedTurfId(newWalkbook.id);

            requestAnimationFrame(() => {
              mapRef.current?.fitToTurf(newWalkbook.id);
            });
          },
        },
      );
    },
    [drawPolygon, createWalkbook, setMode, selectTurf, setLastCreatedTurfId],
  );

  const handleVoterClick = useCallback(
    (voterIds: string[], lngLat: [number, number]) => {
      setSelectedVoter({ ids: voterIds, lngLat });
    },
    [],
  );

  const handleVoterPopupClose = useCallback(() => {
    setSelectedVoter(null);
  }, []);

  // ---------------------------------------------------------------------------
  // Callbacks — turf interaction
  // ---------------------------------------------------------------------------

  const handleTurfClick = useCallback(
    (turfId: string) => {
      selectTurf(turfId);
      mapRef.current?.fitToTurf(turfId);
    },
    [selectTurf],
  );

  const handleEditPolygon = useCallback(
    (turfId: string) => {
      const turf = activeTurfs.find((t) => t.id === turfId);
      if (!turf) return;
      setEditingTurfId(turfId);
      setDrawPolygon(turf.polygon);
      selectTurf(null);
      setMode('cutting');
    },
    [activeTurfs, selectTurf, setMode],
  );

  // ---------------------------------------------------------------------------
  // Loading / error states
  // ---------------------------------------------------------------------------

  if (tokenLoading && !envToken) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (tokenError && !token) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-destructive">{tokenError}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Set VITE_MAPBOX_TOKEN in your .env file or configure the server endpoint.
          </p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Cutting mode
  // ---------------------------------------------------------------------------

  if (mode === 'cutting') {
    const editingPolygon = editingWalkbook
      ? parsePolygon(editingWalkbook.polygon)
      : null;

    return (
      <>
        <div className="relative h-full w-full overflow-hidden">
          <TurfMap
            ref={mapRef}
            token={token}
            turfs={activeTurfs}
            getTurfStatus={getTurfStatus}
            houses={[]}
            selectedTurfId={selectedTurfId}
            onTurfClick={handleTurfClick}
            drawingEnabled
            initialDrawPolygon={editingPolygon}
            onPolygonComplete={handlePolygonForPreview}
            onPolygonEdit={handlePolygonForPreview}
            voterPoints={voterPoints}
            mode="cutting"
            showDemographics={showDemographics}
            demographicMetric={demographicMetric}
            showElectionOverlay={showElectionOverlay}
            showMajorRoads={showMajorRoads}
          />
          {/* Floating cut panel — top-left */}
          <TurfCutPanel
            polygon={drawPolygon}
            previewStats={previewStats}
            isLoadingPreview={isPreviewPending}
            onClear={handleClearPolygon}
            onSave={handleSaveTurf}
            onCancel={handleCancelCut}
            editingTurfName={editingWalkbook?.name}
          />
          {/* VMF-008: Filter warning banner */}
          {hasActiveFilters(voterFilters) && (
            <div className="absolute top-14 left-1/2 -translate-x-1/2 z-30 max-w-lg bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-4 py-2 text-sm flex items-center gap-2">
              <Info className="h-4 w-4 shrink-0" />
              Filters are active — turf will include all voters in the drawn area, not just those currently visible
            </div>
          )}
          {voterPointsFetching && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 rounded-full bg-background/90 px-4 py-2 shadow-md border">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Loading voters...</span>
            </div>
          )}
        </div>
        <SaveTurfDialog
          open={saveDialogOpen}
          onClose={() => setSaveDialogOpen(false)}
          onSave={handleSaveConfirm}
          houseCount={previewStats.houseCount}
          voterCount={previewStats.voterCount}
        />
      </>
    );
  }

  // ---------------------------------------------------------------------------
  // Management mode (default) — Map-first layout
  // ---------------------------------------------------------------------------

  return (
    <div className="relative h-full w-full overflow-hidden">
      {/* Full-bleed map background */}
      {walkbooksLoading ? (
        <div className="flex h-full w-full items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <TurfMap
          ref={mapRef}
          token={token}
          turfs={activeTurfs}
          getTurfStatus={getTurfStatus}
          houses={selectedTurfVoterHouses}
          selectedTurfId={selectedTurfId}
          onTurfClick={handleTurfClick}
          onVoterClick={handleVoterClick}
          canvasserPositions={canvasserPositions}
          voterPoints={voterPoints}
          mode="management"
          showVoterPins={showVoterPins}
          showTurfs={showTurfs}
          showDemographics={showDemographics}
          demographicMetric={demographicMetric}
          showElectionOverlay={showElectionOverlay}
          showMajorRoads={showMajorRoads}
          showContactRate={showContactRate}
          contactRates={contactRates}
        >
          {selectedVoter && (
            <VoterDetailPopup
              voterIds={selectedVoter.ids}
              lngLat={selectedVoter.lngLat}
              onClose={handleVoterPopupClose}
            />
          )}
        </TurfMap>
      )}

      {/* Voter points loading indicator — non-blocking overlay */}
      {voterPointsFetching && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 rounded-full bg-background/90 px-4 py-2 shadow-md border">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Loading voters...</span>
        </div>
      )}

      {/* Left panel — turf list or turf detail */}
      <div
        className={cn(
          'absolute top-4 left-4 z-20 w-80',
          'rounded-2xl bg-white/95 backdrop-blur-sm',
          'shadow-[0_4px_24px_rgba(0,0,0,0.12)]',
          'transition-all duration-300 ease-in-out',
          'flex flex-col overflow-hidden',
          'max-h-[calc(100vh-8rem)]',
        )}
      >
        {selectedTurfId ? (
          <TurfDetailPanel
            onEditPolygon={handleEditPolygon}
            onBack={() => selectTurf(null)}
          />
        ) : (
          <TurfListPanel onCutTurf={handleCutTurf} onTurfClick={handleTurfClick} />
        )}
      </div>

      {/* Floating toolbar — adjacent to left panel */}
      <div className="absolute top-4 left-[22rem] z-10 flex items-center gap-2">
        <VoterFilterPanel />
        <MapLayerPanel />
        {canManageFieldOps && (
          <LiveTrackingToggle
            enabled={showLiveCanvassers}
            onToggle={() => toggle('showLiveCanvassers')}
            activeCount={activeCount}
            connectionState={connectionState}
          />
        )}
      </div>

      {/* Empty state when filters return zero results */}
      {hasActiveFilters(voterFilters) && !voterPoints?.length && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 bg-white/95 backdrop-blur-sm rounded-lg px-6 py-4 shadow-lg text-center">
          <p className="text-sm text-gray-700">No voters match your filters</p>
          <button
            onClick={clearVoterFilters}
            className="mt-2 text-xs text-primary hover:underline"
          >
            Clear filters
          </button>
        </div>
      )}
    </div>
  );
}
